/**
 * Modo "panel inyectado" (editor en la pestaña de la app).
 *
 * La app es la pestaña top-level (login real) y el editor de Veo se inyecta
 * ENCIMA como una tarjeta flotante **arrastrable y redimensionable** (un iframe
 * al dashboard). Además se puede **sacar a una ventana independiente** del
 * navegador ("pop-out").
 *
 * El panel corre en el origen del dashboard (sesión de Veo para guardar guías) y
 * habla con la app (`window.parent` si está anclado, `window.opener` si está en
 * ventana aparte) por el puente postMessage. Este módulo inyecta/gestiona el
 * panel y expone su ventana destino.
 */
import { hasDocument, hasWindow } from '../../utils/safe-env';

const DEFAULT_PANEL_PATH = '/guides/design';
const PANEL_W = 484;
const PANEL_H = 660;
const MIN_W = 320;
const MIN_H = 320;
const MARGIN = 16;
/** Alto del header propio del panel (drag + acciones). */
const HEADER_H = 52;

export interface BuilderPanelOptions {
  dashboardOrigin: string;
  token: string;
  appOrigin: string;
  guide: string | null;
  panelPath: string | null;
  onClose: () => void;
  /** Alto ocupado arriba por el chrome del editor (el panel arranca debajo). */
  topOffset?: number;
}

export interface BuilderPanelHandle {
  /** Ventana destino de los mensajes: el iframe anclado o la ventana pop-out. */
  target(): Window | null;
  /**
   * Rect del IFRAME del panel en coordenadas de la página de la app, o `null`
   * si el editor está en pop-out (ventana aparte: no hay mapeo de coordenadas).
   * Lo usa el drag de bloques para convertir el puntero panel → página.
   */
  frameRect(): DOMRect | null;
  /** Quita el panel del DOM (y cierra la ventana pop-out si hay). */
  teardown(): void;
  /** Título del header (lo publica el editor: «Ajustes de activación», «Paso 1»…). */
  setTitle(title: string): void;
  /** Muestra/oculta la tarjeta (el estado del editor se conserva en el iframe). */
  setVisible(visible: boolean): void;
  isVisible(): boolean;
  /** Reposiciona el tope mínimo cuando el chrome cambia de alto. */
  setTopOffset(px: number): void;
}

/** Inyecta el panel del editor (arrastrable, redimensionable, pop-out). */
export function injectBuilderPanel(opts: BuilderPanelOptions): BuilderPanelHandle {
  if (!hasWindow() || !hasDocument()) {
    return {
      target: () => null,
      frameRect: () => null,
      teardown: () => {},
      setTitle: () => {},
      setVisible: () => {},
      isVisible: () => false,
      setTopOffset: () => {},
    };
  }

  const panelUrl = buildPanelUrl(opts);

  const host = document.createElement('div');
  host.setAttribute('data-veo-builder-panel', '');
  const shadow = host.attachShadow({ mode: 'open' });

  const style = document.createElement('style');
  style.textContent = PANEL_CSS;

  const card = document.createElement('div');
  card.className = 'veo-panel';
  let topOffset = opts.topOffset ?? 0;
  // Mock: la tarjeta arranca a la IZQUIERDA, justo debajo del chrome.
  let left = 40;
  let top = topOffset + MARGIN;
  card.style.left = `${left}px`;
  card.style.top = `${top}px`;
  card.style.width = `${PANEL_W}px`;
  card.style.height = `${PANEL_H}px`;

  // Header (mock 2026-09): grip «···» arriba al centro, título a la izquierda y
  // a la derecha pop-out · maximizar · minimizar.
  const header = document.createElement('div');
  header.className = 'veo-panel-header';
  const grip = document.createElement('span');
  grip.className = 'veo-panel-grip';
  grip.setAttribute('aria-hidden', 'true');
  grip.textContent = '···';
  const title = document.createElement('span');
  title.className = 'veo-panel-title';
  title.textContent = 'OnUser · Editor';
  const actions = document.createElement('div');
  actions.className = 'veo-panel-actions';
  const popout = panelButton(ICON_POPOUT, 'Abrir en ventana aparte');
  const maximize = panelButton(ICON_EXPAND, 'Maximizar');
  const minimize = panelButton(ICON_MINUS, 'Minimizar');
  actions.append(popout, maximize, minimize);
  header.append(grip, title, actions);

  const iframe = document.createElement('iframe');
  iframe.className = 'veo-panel-frame';
  iframe.title = 'Editor de Veo';
  iframe.src = panelUrl;

  const resizeGrip = document.createElement('div');
  resizeGrip.className = 'veo-panel-resize';
  resizeGrip.setAttribute('aria-hidden', 'true');

  card.append(header, iframe, resizeGrip);
  shadow.append(style, card);
  document.body.appendChild(host);

  // ── Minimizar / maximizar ───────────────────────────────────────────────
  let visible = true;
  let maximized = false;
  let restore: { left: number; top: number; w: number; h: number } | null = null;
  const setVisible = (next: boolean): void => {
    visible = next;
    card.style.display = next && !(popped && !popped.closed) ? '' : 'none';
  };
  minimize.addEventListener('click', () => setVisible(false));
  maximize.addEventListener('click', () => {
    maximized = !maximized;
    if (maximized) {
      restore = { left, top, w: card.offsetWidth, h: card.offsetHeight };
      left = MARGIN;
      top = topOffset + MARGIN;
      card.style.left = `${left}px`;
      card.style.top = `${top}px`;
      card.style.width = `${Math.max(MIN_W, window.innerWidth - MARGIN * 2)}px`;
      card.style.height = `${Math.max(MIN_H, window.innerHeight - top - MARGIN)}px`;
      maximize.title = 'Restaurar';
    } else if (restore) {
      left = restore.left;
      top = restore.top;
      card.style.left = `${left}px`;
      card.style.top = `${top}px`;
      card.style.width = `${restore.w}px`;
      card.style.height = `${restore.h}px`;
      maximize.title = 'Maximizar';
    }
  });

  // ── Pop-out: sacar el editor a una ventana independiente ────────────────
  let popped: Window | null = null;
  let pollTimer: ReturnType<typeof setInterval> | undefined;

  const stopPoll = (): void => {
    if (pollTimer !== undefined) {
      clearInterval(pollTimer);
      pollTimer = undefined;
    }
  };

  popout.addEventListener('click', () => {
    const win = window.open(
      panelUrl,
      'veo-editor',
      `popup,width=${Math.round(card.offsetWidth)},height=${Math.round(card.offsetHeight) + 60}`,
    );
    if (!win) return; // bloqueado por el navegador
    popped = win;
    card.style.display = 'none'; // el editor ahora vive en la ventana aparte
    pollTimer = setInterval(() => {
      if (popped?.closed) {
        stopPoll();
        opts.onClose(); // cerrar la ventana = salir del modo editor
      }
    }, 600);
  });

  // ── Arrastre por el header ──────────────────────────────────────────────
  header.addEventListener('mousedown', (e) => {
    if ((e.target as HTMLElement)?.closest('.veo-panel-btn')) return;
    e.preventDefault();
    const sx = e.clientX;
    const sy = e.clientY;
    const ol = left;
    const ot = top;
    iframe.style.pointerEvents = 'none';
    const move = (ev: MouseEvent): void => {
      left = clamp(ol + (ev.clientX - sx), 0, Math.max(0, window.innerWidth - card.offsetWidth));
      top = clamp(ot + (ev.clientY - sy), topOffset, Math.max(topOffset, window.innerHeight - 40));
      card.style.left = `${left}px`;
      card.style.top = `${top}px`;
    };
    const up = (): void => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      iframe.style.pointerEvents = '';
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  });

  // ── Redimensionar por el grip (esquina inferior derecha) ────────────────
  resizeGrip.addEventListener('mousedown', (e) => {
    e.preventDefault();
    e.stopPropagation();
    const sx = e.clientX;
    const sy = e.clientY;
    const sw = card.offsetWidth;
    const sh = card.offsetHeight;
    iframe.style.pointerEvents = 'none';
    const move = (ev: MouseEvent): void => {
      card.style.width = `${clamp(sw + (ev.clientX - sx), MIN_W, window.innerWidth)}px`;
      card.style.height = `${clamp(sh + (ev.clientY - sy), MIN_H, window.innerHeight)}px`;
    };
    const up = (): void => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      iframe.style.pointerEvents = '';
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  });

  return {
    target: () => (popped && !popped.closed ? popped : iframe.contentWindow),
    frameRect: () => (popped && !popped.closed ? null : iframe.getBoundingClientRect()),
    teardown: () => {
      stopPoll();
      if (popped && !popped.closed) popped.close();
      host.remove();
    },
    setTitle: (t) => {
      title.textContent = t;
    },
    setVisible,
    isVisible: () => visible,
    setTopOffset: (px) => {
      topOffset = px;
      if (top < topOffset + MARGIN) {
        top = topOffset + MARGIN;
        card.style.top = `${top}px`;
      }
    },
  };
}

const ICON_POPOUT =
  '<path stroke-linecap="round" stroke-linejoin="round" d="m6.75 7.5 3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0 0 21 18V6a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6v12a2.25 2.25 0 0 0 2.25 2.25Z"/>';
const ICON_EXPAND =
  '<path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"/>';
const ICON_MINUS = '<path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14"/>';

function panelButton(iconPath: string, label: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.className = 'veo-panel-btn';
  b.type = 'button';
  b.title = label;
  b.setAttribute('aria-label', label);
  b.innerHTML =
    '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" ' +
    `fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">${iconPath}</svg>`;
  return b;
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(Math.max(v, min), max);
}

function buildPanelUrl(opts: BuilderPanelOptions): string {
  const path = opts.panelPath ?? DEFAULT_PANEL_PATH;
  const url = new URL(path, opts.dashboardOrigin);
  url.searchParams.set('veoToken', opts.token);
  url.searchParams.set('veoApp', opts.appOrigin);
  if (opts.guide) url.searchParams.set('veoGuide', opts.guide);
  return url.toString();
}

const PANEL_CSS = `
:host { all: initial; }
.veo-panel {
  position: fixed;
  min-width: ${MIN_W}px; min-height: ${MIN_H}px;
  max-width: 96vw; max-height: 94vh;
  display: flex; flex-direction: column;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.22);
  overflow: hidden;
  z-index: 2147483647;
  font-family: system-ui, -apple-system, sans-serif;
}
.veo-panel-header {
  position: relative; flex: 0 0 auto; height: ${HEADER_H}px; box-sizing: border-box;
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 14px 0 22px;
  background: #fbf8f5; color: #1c1917; border-bottom: 1px solid #e8e2dc;
  font-size: 17px; font-weight: 700;
  cursor: grab;
}
.veo-panel-header:active { cursor: grabbing; }
.veo-panel-grip {
  position: absolute; top: 0; left: 50%; transform: translateX(-50%);
  color: #b5aca4; font-size: 14px; letter-spacing: 1px; line-height: 12px;
}
.veo-panel-title { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.veo-panel-actions { display: flex; gap: 2px; }
.veo-panel-btn {
  border: 0; background: transparent; color: #4b4540;
  cursor: pointer; line-height: 1; display: inline-flex; align-items: center;
  padding: 6px; border-radius: 8px;
}
.veo-panel-btn:hover { background: rgba(0,0,0,0.06); color: #1c1917; }
.veo-panel-frame { flex: 1 1 auto; width: 100%; border: 0; background: #fff; }
.veo-panel-resize {
  position: absolute; right: 0; bottom: 0;
  width: 16px; height: 16px;
  cursor: nwse-resize;
  background:
    linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.25) 60%,
    transparent 60%, transparent 72%, rgba(0,0,0,0.25) 72%, rgba(0,0,0,0.25) 82%, transparent 82%);
}
`;
